import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Loading } from '@/components/Loading';
import { ticketService } from '@/services/ticketService';
import { userService } from '@/services/userService';
import { TicketPriority, TicketSource, UserRole } from '@/types/api';
import type { User, Company } from '@/types/api';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

// Validation schema
const createTicketSchema = z.object({
  subject: z.string()
    .min(1, 'Subject is required')
    .min(5, 'Subject must be at least 5 characters')
    .max(200, 'Subject must be less than 200 characters'),
  description: z.string()
    .min(1, 'Description is required')
    .min(10, 'Description must be at least 10 characters')
    .max(5000, 'Description must be less than 5000 characters'),
  priority: z.nativeEnum(TicketPriority).optional(),
  source: z.nativeEnum(TicketSource).optional(),
  tags: z.string().optional(),
  requesterId: z.string().optional(),
  assignedToId: z.string().optional(),
  companyId: z.string().optional(),
});

type CreateTicketFormData = z.infer<typeof createTicketSchema>;

/**
 * Create Ticket Page Component
 * 
 * Provides form for creating new support tickets.
 * Shows different fields based on user role.
 */
export function CreateTicket() {
  useDocumentTitle('Create New Support Ticket | SupportDesk');
  const navigate = useNavigate();
  const { user, hasAnyRole } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agents, setAgents] = useState<User[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<CreateTicketFormData>({
    resolver: zodResolver(createTicketSchema),
    defaultValues: {
      subject: '',
      description: '',
      priority: TicketPriority.Medium,
      source: TicketSource.WebForm,
      tags: '',
      requesterId: user?.role === UserRole.EndUser ? user.id : undefined,
      assignedToId: '',
      companyId: user?.companyId,
    },
  });

  // Load dropdown options for agents/admins
  useEffect(() => {
    const loadOptions = async () => {
      if (!hasAnyRole([UserRole.Admin, UserRole.Agent])) return;

      try {
        setIsLoadingOptions(true);
        
        // Load agents and users in parallel
        const [agentsResponse, usersResponse] = await Promise.all([
          userService.getUsers({ role: [UserRole.Agent, UserRole.Admin] }),
          userService.getUsers({ role: [UserRole.EndUser] })
        ]);

        if (agentsResponse.success && agentsResponse.data) {
          setAgents(agentsResponse.data.data || []);
        }

        if (usersResponse.success && usersResponse.data) {
          setUsers(usersResponse.data.data || []);
        }

        // TODO: Load companies when company service is implemented
        // For now, use empty array
        setCompanies([]);
        
      } catch (err) {
        console.error('Failed to load dropdown options:', err);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadOptions();
  }, [hasAnyRole]);

  const description = watch('description');
  const descriptionLength = description?.length || 0;

  const onSubmit = async (data: CreateTicketFormData) => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Process tags
      const tags = data.tags 
        ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean)
        : [];

      // Create ticket payload
      const ticketPayload = {
        subject: data.subject,
        description: data.description,
        priority: data.priority || TicketPriority.Medium,
        source: data.source || TicketSource.WebForm,
        tags,
        requesterId: data.requesterId || (user?.role === UserRole.EndUser ? user.id : undefined),
        assignedToId: data.assignedToId || undefined,
        companyId: data.companyId || user?.companyId,
        customFields: {}
      };

      // Call real API
      const response = await ticketService.createTicket(ticketPayload);
      
      if (response.success && response.data) {
        // Navigate to the new ticket with proper context
        navigate(`/tickets/${response.data.id}`, { 
          state: { 
            from: '/tickets/create',
            context: {
              type: 'newTicket',
              value: 'created'
            },
            message: 'Ticket created successfully!',
            ticket: response.data
          }
        });
      } else {
        throw new Error(response.message || 'Failed to create ticket');
      }
    } catch (err) {
      console.error('Failed to create ticket:', err);
      setError(err instanceof Error ? err.message : 'Failed to create ticket');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case TicketPriority.Urgent:
        return 'text-red-600';
      case TicketPriority.High:
        return 'text-orange-600';
      case TicketPriority.Medium:
        return 'text-yellow-600';
      case TicketPriority.Low:
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  const isEndUser = user.role === UserRole.EndUser;
  const canAssignTickets = hasAnyRole([UserRole.Admin, UserRole.Agent]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="py-6 md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">
                Create New Support Ticket
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Provide details about your issue or request
              </p>
            </div>
            <div className="mt-6 flex space-x-3 md:mt-0 md:ml-4">
              <Button
                variant="outline"
                onClick={() => navigate('/tickets')}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <Card title="Ticket Information">
            <div className="p-6 space-y-6">
              {/* Error Display */}
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Subject */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                  Subject <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <Input
                    id="subject"
                    type="text"
                    placeholder="Brief description of your issue or request"
                    error={errors.subject?.message}
                    {...register('subject')}
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Provide a clear, concise summary of your issue
                </p>
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description <span className="text-red-500">*</span>
                </label>
                <div className="mt-1">
                  <textarea
                    id="description"
                    rows={6}
                    className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm ${
                      errors.description ? 'border-red-300' : ''
                    }`}
                    placeholder="Provide detailed information about your issue, including steps to reproduce, error messages, and any relevant context..."
                    {...register('description')}
                  />
                </div>
                <div className="mt-1 flex justify-between">
                  <div>
                    {errors.description && (
                      <p className="text-sm text-red-600">{errors.description.message}</p>
                    )}
                  </div>
                  <p className={`text-sm ${
                    descriptionLength > 4800 ? 'text-red-600' : 
                    descriptionLength > 4500 ? 'text-yellow-600' : 'text-gray-500'
                  }`}>
                    {descriptionLength}/5000 characters
                  </p>
                </div>
              </div>

              {/* Priority */}
              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                  Priority
                </label>
                <div className="mt-1">
                  <select
                    id="priority"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                    {...register('priority')}
                  >
                    <option value={TicketPriority.Low}>Low - General questions, feature requests</option>
                    <option value={TicketPriority.Medium}>Medium - Issues affecting your work</option>
                    <option value={TicketPriority.High}>High - Significant problems, multiple users affected</option>
                    <option value={TicketPriority.Urgent}>Urgent - System down, critical business impact</option>
                  </select>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Select the priority based on the business impact
                </p>
              </div>

              {/* Tags */}
              <div>
                <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                  Tags
                </label>
                <div className="mt-1">
                  <Input
                    id="tags"
                    type="text"
                    placeholder="email, mobile, billing (comma separated)"
                    {...register('tags')}
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Add relevant tags to help categorize your ticket (optional)
                </p>
              </div>
            </div>
          </Card>

          {/* Administrative Fields - Only for Agents/Admins */}
          {canAssignTickets && (
            <Card title="Administrative Options">
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Source */}
                  <div>
                    <label htmlFor="source" className="block text-sm font-medium text-gray-700">
                      Source
                    </label>
                    <div className="mt-1">
                      <select
                        id="source"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        {...register('source')}
                      >
                        <option value={TicketSource.WebForm}>Web Form</option>
                        <option value={TicketSource.Email}>Email</option>
                        <option value={TicketSource.Phone}>Phone</option>
                        <option value={TicketSource.Chat}>Live Chat</option>
                        <option value={TicketSource.API}>API</option>
                      </select>
                    </div>
                  </div>

                  {/* Requester (for agents creating on behalf of users) */}
                  <div>
                    <label htmlFor="requesterId" className="block text-sm font-medium text-gray-700">
                      Requester
                    </label>
                    <div className="mt-1">
                      <select
                        id="requesterId"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        {...register('requesterId')}
                      >
                        <option value="">Select a user (optional)</option>
                        {isLoadingOptions ? (
                          <option disabled>Loading users...</option>
                        ) : (
                          users.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.fullName} ({user.email})
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Leave empty to create ticket for yourself
                    </p>
                  </div>

                  {/* Assigned To */}
                  <div>
                    <label htmlFor="assignedToId" className="block text-sm font-medium text-gray-700">
                      Assign to Agent
                    </label>
                    <div className="mt-1">
                      <select
                        id="assignedToId"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        {...register('assignedToId')}
                      >
                        <option value="">Unassigned</option>
                        {isLoadingOptions ? (
                          <option disabled>Loading agents...</option>
                        ) : (
                          agents.map((agent) => (
                            <option key={agent.id} value={agent.id}>
                              {agent.fullName} ({agent.email})
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>

                  {/* Company */}
                  <div>
                    <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">
                      Company
                    </label>
                    <div className="mt-1">
                      <select
                        id="companyId"
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                        {...register('companyId')}
                      >
                        <option value="">No Company</option>
                        {isLoadingOptions ? (
                          <option disabled>Loading companies...</option>
                        ) : (
                          companies.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Form Actions */}
          <Card>
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {isEndUser ? 
                    'Your ticket will be assigned to the next available agent' :
                    'You can assign this ticket now or leave it in the queue'
                  }
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/tickets')}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Creating...' : 'Create Ticket'}
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </form>
      </div>

      {/* Help Section */}
      <Card title="Need Help?">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="mx-auto h-8 w-8 text-primary-600 mb-3">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">
                Check Help Articles
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                Search our knowledge base before creating a ticket
              </p>
              <Button variant="outline" size="sm">
                Browse Help
              </Button>
            </div>

            <div className="text-center">
              <div className="mx-auto h-8 w-8 text-primary-600 mb-3">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">
                Live Chat Support
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                Get instant help from our support team
              </p>
              <Button variant="outline" size="sm">
                Start Chat
              </Button>
            </div>

            <div className="text-center">
              <div className="mx-auto h-8 w-8 text-primary-600 mb-3">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">
                Phone Support
              </h3>
              <p className="text-sm text-gray-500 mb-3">
                Call us for urgent issues requiring immediate attention
              </p>
              <Button variant="outline" size="sm">
                Call Now
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}